import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Alert, 
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { 
  FileText, 
  Edit, 
  Check, 
  Download, 
  Send, 
  Calendar, 
  Filter, 
  ChevronRight,
  Tag,
  AlertTriangle,
  Image,
  Plus,
  Trash2,
  Code
} from 'lucide-react';
import { format } from 'date-fns';
import MissingReceiptLedger from './MissingReceiptLedger';
import BankStatementViewer from '../BankStatementViewer';
import WebPortalAllocationPreview from '../WebPortalAllocationPreview';
import GLCodeField from './GLCodeField';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ExpenseReportReviewer = () => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportDataLoading, setReportDataLoading] = useState(false);
  const [glEditMode, setGLEditMode] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [adminSignature, setAdminSignature] = useState('');
  const [isSigningReport, setIsSigningReport] = useState(false);
  const [savingGLCodes, setSavingGLCodes] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [markingAsSent, setMarkingAsSent] = useState(false);
  const [confirmSendDialog, setConfirmSendDialog] = useState(false);
  const [sendToBillingDialog, setSendToBillingDialog] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [sendingToBilling, setSendingToBilling] = useState(false);
  
  // Add new state for editing expense items
  const [editItemDialog, setEditItemDialog] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItemData, setEditItemData] = useState({});
  const [savingItemEdit, setSavingItemEdit] = useState(false);
  
  // Add new state for re-send functionality
  const [resendToBillingDialog, setResendToBillingDialog] = useState(false);
  const [resendBillingEmail, setResendBillingEmail] = useState('');
  const [resendingToBilling, setResendingToBilling] = useState(false);
  
  // Add new state for GL code inline edit dialog
  const [glCodeDialog, setGLCodeDialog] = useState(false);
  const [glCodeDialogItem, setGLCodeDialogItem] = useState(null);
  const [glCodeDialogValue, setGLCodeDialogValue] = useState('');
  const [savingGLCodeDialog, setSavingGLCodeDialog] = useState(false);
  
  // Add new state for GL splits functionality
  const [glSplitsDialog, setGLSplitsDialog] = useState(false);
  const [selectedItemForSplits, setSelectedItemForSplits] = useState(null);
  const [glSplits, setGLSplits] = useState([]);
  const [savingGLSplits, setSavingGLSplits] = useState(false);
  const [splitMode, setSplitMode] = useState('percentage'); // 'percentage' or 'dollar'
  const [branchOptions, setBranchOptions] = useState([]);
  
  useEffect(() => {
    fetchReports();
  }, [selectedMonth, selectedYear, statusFilter, includeArchived]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const data = await apiService.getBranches();
        setBranchOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching branches:', err);
        setError('Could not load branches for GL splits');
      }
    };
    loadBranches();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiService.getReportsForReview({
        month: selectedMonth,
        year: selectedYear,
        status: statusFilter,
        includeArchived
      });
      
      setReports(data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError(`Error fetching reports: ${err.message}`);
      setLoading(false);
    }
  };

  const handleSelectReport = async (report) => {
    try {
      setSelectedReport(report);
      setReportDataLoading(true);
      setError(null);
      
      const data = await apiService.getReportForReview(report.id);
      
      setReportData(data);
      
      // Initialize edited items with current GL codes
      if (data && data.items) {
        setEditedItems(data.items.map(item => ({
          id: item.id,
          gl_account: item.gl_account || ''
        })));
      }
      
      setReportDataLoading(false);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(`Error fetching report data: ${err.message}`);
      setReportDataLoading(false);
    }
  };

  const handleGLCodeChange = (itemId, newCode) => {
    setEditedItems(items => 
      items.map(item => 
        item.id === itemId ? { ...item, gl_account: newCode } : item
      )
    );
  };

  const handleOpenGLCodeDialog = (item) => {
    setGLCodeDialogItem(item);
    setGLCodeDialogValue(editedItems.find(i => i.id === item.id)?.gl_account || item.gl_account || '');
    setGLCodeDialog(true);
  };

  const handleSaveGLCodeDialog = async () => {
    if (!glCodeDialogItem) return;
    
    try {
      setSavingGLCodeDialog(true);
      setError(null);
      
      const updatedItems = editedItems.map(item => 
        item.id === glCodeDialogItem.id ? { ...item, gl_account: glCodeDialogValue } : item
      );
      setEditedItems(updatedItems);
      
      await apiService.updateGLCodes(selectedReport.id, updatedItems);
      
      setSuccess('GL code updated successfully');
      setGLCodeDialog(false);
      setGLCodeDialogItem(null);
      
      await handleSelectReport(selectedReport);
      
      setSavingGLCodeDialog(false);
    } catch (err) {
      console.error('Error saving GL code:', err);
      setError(`Error saving GL code: ${err.message}`);
      setSavingGLCodeDialog(false);
    }
  };

  const handleSaveGLCodes = async () => {
    try {
      setSavingGLCodes(true);
      setError(null);
      
      await apiService.updateGLCodes(selectedReport.id, editedItems);
      
      setSuccess('GL codes updated successfully');
      setGLEditMode(false);
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setSavingGLCodes(false);
    } catch (err) {
      console.error('Error saving GL codes:', err);
      setError(`Error saving GL codes: ${err.message}`);
      setSavingGLCodes(false);
    }
  };

  const handleGenerateFinalPDF = async () => {
    try {
      setGeneratingPDF(true);
      setError(null);
      
      const result = await apiService.generateFinalPDF(selectedReport.id);
      
      // Open the URL in a new tab if available
      if (result && result.url) {
        window.open(result.url, '_blank');
      }
      
      setSuccess('Final PDF generated successfully');
      
      // Refresh reports to update status
      await fetchReports();
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setGeneratingPDF(false);
    } catch (err) {
      console.error('Error generating final PDF:', err);
      setError(`Error generating final PDF: ${err.message}`);
      setGeneratingPDF(false);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSendToBilling = async () => {
    if (!billingEmail.trim() || !validateEmail(billingEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setSendingToBilling(true);
      setError(null);
      
      await apiService.sendReportToBilling(selectedReport.id, billingEmail);
      
      setSuccess('Report successfully sent to billing');
      setSendToBillingDialog(false);
      
      // Refresh reports to update status
      await fetchReports();
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setSendingToBilling(false);
    } catch (err) {
      console.error('Error sending report to billing:', err);
      setError(`Error sending to billing: ${err.message}`);
      setSendingToBilling(false);
    }
  };

  const handleMarkSentToBilling = async () => {
    try {
      setMarkingAsSent(true);
      setError(null);
      
      await apiService.markSentToBilling(selectedReport.id);
      
      setSuccess('Report marked as sent to billing successfully');
      setConfirmSendDialog(false);
      
      // Refresh reports to update status
      await fetchReports();
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setMarkingAsSent(false);
    } catch (err) {
      console.error('Error marking report as sent to billing:', err);
      setError(`Error marking report as sent to billing: ${err.message}`);
      setMarkingAsSent(false);
    }
  };

  const handleAdminSign = async () => {
    if (!adminSignature) {
      setError('Admin signature is required');
      return;
    }
    
    setIsSigningReport(true);
    try {
      await apiService.adminSignReport(reportData.report.id, adminSignature);
      await fetchReports();
      setSuccess('Report signed successfully');
      setAdminSignature('');
    } catch (error) {
      setError('Error signing report: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSigningReport(false);
    }
  };

  // Add new handlers for editing expense items
  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setEditItemData({
      work_order: item.work_order || '',
      date: item.date || '',
      description: item.description || '',
      amount: item.amount || '',
      gl_account: item.gl_account || ''
    });
    setEditItemDialog(true);
  };

  const handleCancelItemEdit = () => {
    setEditItemDialog(false);
    setEditingItemId(null);
    setEditItemData({});
  };

  const handleSaveItemEdit = async () => {
    try {
      setSavingItemEdit(true);
      setError(null);

      await apiService.updateExpenseItem(selectedReport.id, editingItemId, editItemData);
      
      setSuccess('Expense item updated successfully');
      setEditItemDialog(false);
      setEditingItemId(null);
      setEditItemData({});
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setSavingItemEdit(false);
    } catch (err) {
      console.error('Error updating expense item:', err);
      setError(`Error updating expense item: ${err.message}`);
      setSavingItemEdit(false);
    }
  };

  const handleItemDataChange = (field, value) => {
    setEditItemData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Re-send to billing: regenerate PDF with latest data, then send
  const handleResendToBilling = async () => {
    if (!resendBillingEmail.trim() || !validateEmail(resendBillingEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setResendingToBilling(true);
      setError(null);
      
      // Regenerate the final PDF to include any corrections made
      await apiService.generateFinalPDF(selectedReport.id);
      
      // Now send the freshly-generated PDF
      await apiService.sendReportToBilling(selectedReport.id, resendBillingEmail);
      
      setSuccess('Report regenerated and successfully re-sent to billing');
      setResendToBillingDialog(false);
      
      // Refresh reports to update status
      await fetchReports();
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setResendingToBilling(false);
    } catch (err) {
      console.error('Error re-sending report to billing:', err);
      setError(`Error re-sending to billing: ${err.message}`);
      setResendingToBilling(false);
    }
  };

  // GL Splits handlers
  const handleOpenGLSplits = async (item) => {
    try {
      setSelectedItemForSplits(item);
      setError(null);
      
      // Check if splits are already loaded in the item data
      if (item.has_splits && item.gl_splits && item.gl_splits.length > 0) {
        setGLSplits(item.gl_splits);
        setGLSplitsDialog(true);
        return;
      }
      
      // Get existing splits for this item from API
      const response = await apiService.getGLSplits(selectedReport.id, item.id);
      
      if (response.splits && response.splits.length > 0) {
        setGLSplits(response.splits);
      } else {
        // Initialize with one split using the current GL code if it exists
        setGLSplits([{
          gl_code: item.gl_account || '',
          amount: item.amount,
          percentage: 100,
          branch: '',
          description: ''
        }]);
      }
      
      setGLSplitsDialog(true);
    } catch (err) {
      console.error('Error loading GL splits:', err);
      setError(`Error loading GL splits: ${err.message}`);
    }
  };

  const handleAddGLSplit = () => {
    const newSplit = {
      gl_code: '',
      amount: 0,
      percentage: 0,
      branch: '',
      description: ''
    };
    
    const newSplits = [...glSplits, newSplit];
    
    // Auto-distribute remaining amount/percentage if we have an item selected
    if (selectedItemForSplits) {
      const itemAmount = parseFloat(selectedItemForSplits.amount);
      const distributedSplits = autoDistributeRemaining(newSplits, itemAmount);
      setGLSplits(distributedSplits);
    } else {
      setGLSplits(newSplits);
    }
  };

  const handleRemoveGLSplit = (index) => {
    const newSplits = glSplits.filter((_, i) => i !== index);
    setGLSplits(newSplits);
  };

  const handleGLSplitChange = (index, field, value) => {
    const newSplits = [...glSplits];
    newSplits[index] = { ...newSplits[index], [field]: value };
    
    if (!selectedItemForSplits) {
      setGLSplits(newSplits);
      return;
    }
    
    const itemAmount = parseFloat(selectedItemForSplits.amount);
    
    // If amount is changed, recalculate percentage
    if (field === 'amount') {
      const percentage = (parseFloat(value || 0) / itemAmount) * 100;
      newSplits[index].percentage = percentage.toFixed(2);
    }
    
    // If percentage is changed, recalculate amount
    if (field === 'percentage') {
      const amount = (parseFloat(value || 0) / 100) * itemAmount;
      newSplits[index].amount = amount.toFixed(2);
    }
    
    setGLSplits(newSplits);
  };

  // Auto-distribute remaining percentage/amount when splits are added
  const autoDistributeRemaining = (splits, itemAmount) => {
    if (splits.length <= 1) return splits;
    
    const updatedSplits = [...splits];
    
    if (splitMode === 'percentage') {
      // Calculate total assigned percentage (excluding the last split)
      const assignedPercentage = updatedSplits.slice(0, -1).reduce((sum, split) => 
        sum + parseFloat(split.percentage || 0), 0);
      
      // Assign remaining percentage to the last split
      const remainingPercentage = Math.max(0, 100 - assignedPercentage);
      const lastIndex = updatedSplits.length - 1;
      updatedSplits[lastIndex].percentage = remainingPercentage.toFixed(2);
      updatedSplits[lastIndex].amount = ((remainingPercentage / 100) * itemAmount).toFixed(2);
    } else {
      // Calculate total assigned amount (excluding the last split)
      const assignedAmount = updatedSplits.slice(0, -1).reduce((sum, split) => 
        sum + parseFloat(split.amount || 0), 0);
      
      // Keep the last split on the same side of zero as the expense item so refunds stay negative
      const rawRemaining = itemAmount - assignedAmount;
      const remainingAmount = itemAmount < 0
        ? Math.min(0, rawRemaining)
        : Math.max(0, rawRemaining);
      const lastIndex = updatedSplits.length - 1;
      updatedSplits[lastIndex].amount = remainingAmount.toFixed(2);
      updatedSplits[lastIndex].percentage = ((remainingAmount / itemAmount) * 100).toFixed(2);
    }
    
    return updatedSplits;
  };

  const handleSaveGLSplits = async () => {
    try {
      setSavingGLSplits(true);
      setError(null);

      // Validate splits
      const totalAmount = glSplits.reduce((sum, split) => sum + parseFloat(split.amount || 0), 0);
      const itemAmount = parseFloat(selectedItemForSplits.amount);
      
      if (Math.abs(totalAmount - itemAmount) > 0.01) {
        setError(`Split amounts must total exactly $${itemAmount.toFixed(2)}. Current total: $${totalAmount.toFixed(2)}`);
        setSavingGLSplits(false);
        return;
      }

      const validBranchNames = new Set(branchOptions.map((branch) => branch.name));

      // Validate required fields
      for (const split of glSplits) {
        const splitAmount = parseFloat(split.amount);
        if (!split.gl_code || !Number.isFinite(splitAmount) || splitAmount === 0) {
          setError('Each split must have a GL code and a non-zero amount. Refund amounts can be negative.');
          setSavingGLSplits(false);
          return;
        }
        if (!split.branch || !validBranchNames.has(split.branch)) {
          setError('Each split must use a branch from the list');
          setSavingGLSplits(false);
          return;
        }
      }

      await apiService.createGLSplits(selectedReport.id, selectedItemForSplits.id, glSplits);
      
      setSuccess('GL splits saved successfully');
      setGLSplitsDialog(false);
      
      // Refresh report data to show splits
      await handleSelectReport(selectedReport);
      
      setSavingGLSplits(false);
    } catch (err) {
      console.error('Error saving GL splits:', err);
      setError(`Error saving GL splits: ${err.message}`);
      setSavingGLSplits(false);
    }
  };

  const handleDeleteGLSplits = async () => {
    try {
      setSavingGLSplits(true);
      setError(null);

      await apiService.deleteGLSplits(selectedReport.id, selectedItemForSplits.id);
      
      setSuccess('GL splits deleted successfully');
      setGLSplitsDialog(false);
      
      // Refresh report data
      await handleSelectReport(selectedReport);
      
      setSavingGLSplits(false);
    } catch (err) {
      console.error('Error deleting GL splits:', err);
      setError(`Error deleting GL splits: ${err.message}`);
      setSavingGLSplits(false);
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'draft':
        return <Chip label="Draft" color="default" size="small" />;
      case 'not_started':
        return <Chip label="Not Started" color="error" size="small" />;
      case 'no_bank_statement':
        return <Chip label="No Bank Statement" color="warning" size="small" />;
      case 'submitted':
        return <Chip label="Submitted" color="primary" size="small" />;
      case 'review':
        return <Chip label="In Review" color="secondary" size="small" />;
      case 'gl_coded':
        return <Chip label="GL Coded" color="info" size="small" />;
      case 'final':
        return <Chip label="Finalized" color="success" size="small" />;
      case 'sent_to_billing':
        return <Chip label="Sent to Billing" color="warning" size="small" />;
      default:
        return <Chip label={status} color="default" size="small" />;
    }
  };

  const canEditGLCodes = reportData && 
    (reportData.report.workflow_status === 'submitted' || 
     reportData.report.workflow_status === 'review' ||
     reportData.report.workflow_status === 'gl_coded' ||
     reportData.report.workflow_status === 'final' ||
     reportData.report.workflow_status === 'sent_to_billing');

  const itemNeedsGlCode = (item) => {
    if (!item) return false;
    if (item.is_web_portal) return false;
    if (item.has_splits && item.gl_splits && item.gl_splits.length > 0) return false;
    return !item.gl_account;
  };

  const allItemsCoded = reportData &&
    reportData.items.length > 0 &&
    reportData.items.every((item) => !itemNeedsGlCode(item));

  const canGenerateFinalPDF = reportData &&
    allItemsCoded &&
    (reportData.report.workflow_status === 'gl_coded' ||
     reportData.report.workflow_status === 'final' ||
     reportData.report.workflow_status === 'sent_to_billing' ||
     reportData.report.workflow_status === 'submitted' ||
     reportData.report.workflow_status === 'review');

  const canMarkAsSent = reportData &&
    reportData.report.workflow_status === 'final';

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 3 }, (_, i) => {
    const year = new Date().getFullYear() - 1 + i;
    return { value: year, label: year.toString() };
  });

  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'not_started', label: 'Not Started' },
    { value: 'no_bank_statement', label: 'No Bank Statement' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'review', label: 'In Review' },
    { value: 'gl_coded', label: 'GL Coded' },
    { value: 'final', label: 'Finalized' },
    { value: 'sent_to_billing', label: 'Sent to Billing' }
  ];

  // Function to display date without timezone shift
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    
    // Handle ISO date string with timezone (e.g., "2025-04-03T00:00:00.000Z")
    if (dateStr.includes('T')) {
      // Just take the date part before the 'T'
      dateStr = dateStr.split('T')[0];
    }
    
    // Split date string into parts (expected format: "YYYY-MM-DD")
    const parts = dateStr.split('-');
    
    // Format as MM/DD/YYYY with leading zeros
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1].padStart(2, '0');
      const day = parts[2].padStart(2, '0');
      return `${month}/${day}/${year}`;
    }
    
    // Fallback to the original value if not in expected format
    return dateStr;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Expense Report Review
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left sidebar - Reports list */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Filter size={20} style={{ marginRight: 8 }} />
              <Typography variant="subtitle1">Filters</Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                >
                  {years.map((year) => (
                    <MenuItem key={year.value} value={year.value}>
                      {year.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeArchived}
                      onChange={(e) => setIncludeArchived(e.target.checked)}
                    />
                  }
                  label="Include archived reports"
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Reports
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : reports.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                                {reports.map((report) => {
                  const isSpecialStatus = statusFilter === 'not_started' || statusFilter === 'no_bank_statement';
                  const reportKey = report.id || `${report.user_id}-${report.month}-${report.year}`;
                  
                  return (
                    <Card 
                      key={reportKey} 
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        cursor: isSpecialStatus ? 'default' : 'pointer',
                        bgcolor: selectedReport && selectedReport.id === report.id ? '#f0f7ff' : 'transparent',
                        '&:hover': { bgcolor: isSpecialStatus ? 'transparent' : '#f5f5f5' },
                        transition: 'background-color 0.2s',
                        opacity: isSpecialStatus ? 0.8 : 1
                      }}
                      onClick={isSpecialStatus ? undefined : () => handleSelectReport(report)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2">
                            {report.username}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {report.archived && (
                              <Chip 
                                label="Archived" 
                                size="small" 
                                color="secondary"
                                sx={{ mr: 1 }}
                              />
                            )}
                            {getStatusChip(report.workflow_status)}
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Month: {months.find(m => m.value === report.month)?.label} {report.year}
                        </Typography>
                        
                        {statusFilter === 'no_bank_statement' ? (
                          <Typography variant="body2" color="text.secondary">
                            No expenses for this period
                          </Typography>
                        ) : statusFilter === 'not_started' ? (
                          <Typography variant="body2" color="text.secondary">
                            Bank statement sent - awaiting expense entry
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Total: ${report.total_amount ? parseFloat(report.total_amount).toFixed(2) : '0.00'}
                          </Typography>
                        )}
                        
                        {!isSpecialStatus && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <ChevronRight size={16} />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No reports found for the selected filters
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right section - Report details */}
        <Grid item xs={12} md={8}>
          {reportDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedReport && reportData ? (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6">
                  {selectedReport.username}'s Expense Report
                </Typography>
                {getStatusChip(reportData.report.workflow_status)}
              </Box>
              


              <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Month</Typography>
                  <Typography variant="body1">
                    {months.find(m => m.value === reportData.report.month)?.label} {reportData.report.year}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                  <Typography variant="body1">
                    ${reportData.report.total_amount ? parseFloat(reportData.report.total_amount).toFixed(2) : '0.00'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Submission Date</Typography>
                  <Typography variant="body1">
                    {reportData.report.submitted_at ? format(new Date(reportData.report.submitted_at), 'MM/dd/yyyy') : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
                <Tab label="Expenses" />
                <Tab label="Bank Statement" />
                <Tab label="Missing Receipt Ledger" />
                <Tab label="Receipts" />
              </Tabs>

              <TabPanel value={activeTab} index={0}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Expense Items</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      
                      {/* GL Codes Edit */}
                      {canEditGLCodes && (
                        glEditMode ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              onClick={() => setGLEditMode(false)}
                              disabled={savingGLCodes}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              onClick={handleSaveGLCodes}
                              disabled={savingGLCodes}
                              startIcon={savingGLCodes ? <CircularProgress size={20} /> : <Check size={20} />}
                            >
                              {savingGLCodes ? 'Saving...' : 'Save GL Codes'}
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            startIcon={<Edit size={20} />}
                            onClick={() => setGLEditMode(true)}
                          >
                            Edit GL Codes
                          </Button>
                        )
                      )}
                    </Box>
                  </Box>

                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ whiteSpace: 'nowrap', px: 1 }}>Date</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', px: 1 }}>W.O.</TableCell>
                          <TableCell sx={{ maxWidth: '150px', px: 1 }}>Description</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', px: 1 }}>Amount</TableCell>
                          <TableCell sx={{ minWidth: '120px', px: 1 }}>GL Code</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', px: 1 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', px: 1 }}>
                              {formatDateDisplay(item.date)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', px: 1 }}>
                              {item.work_order || '-'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: '150px', px: 1 }}>
                              <Tooltip title={item.description} placement="top-start" arrow>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      overflow: 'hidden', 
                                      textOverflow: 'ellipsis', 
                                      whiteSpace: 'nowrap',
                                      fontSize: '0.8rem'
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                  {item.is_web_portal && (
                                    <Chip label="Web Portal" size="small" color="primary" variant="outlined" />
                                  )}
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', px: 1 }}>
                              ${parseFloat(item.amount).toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ px: 1 }}>
                              {item.is_web_portal ? (
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                                  Web Portal
                                </Typography>
                              ) : glEditMode ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TextField
                                    size="small"
                                    value={editedItems.find(i => i.id === item.id)?.gl_account || ''}
                                    onChange={(e) => handleGLCodeChange(item.id, e.target.value)}
                                    placeholder="GL code"
                                    sx={{ 
                                      minWidth: '70px',
                                      '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '4px 6px' }
                                    }}
                                  />
                                  <Tooltip title="Open GL code editor with full description">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleOpenGLCodeDialog(item)}
                                      color="primary"
                                      sx={{ p: 0.5 }}
                                    >
                                      <Code size={14} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {item.has_splits ? (
                                      <Box>
                                        <Tooltip title="Click to edit GL splits">
                                          <Chip 
                                            label={`Split (${item.gl_splits?.length || 0})`} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                            onClick={() => handleOpenGLSplits(item)}
                                            sx={{ cursor: 'pointer', mb: 0.5 }}
                                          />
                                        </Tooltip>
                                        {item.gl_splits && item.gl_splits.length > 0 && (
                                          <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
                                            {item.gl_splits.map((split, index) => (
                                              <div key={index}>
                                                {split.gl_code} ({split.percentage}%)
                                                {split.branch && ` - ${split.branch}`}
                                              </div>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {item.gl_account || '-'}
                                      </Typography>
                                    )}
                                  </Box>
                                  {canEditGLCodes && !item.is_web_portal && (
                                    <Tooltip title={item.has_splits ? 'Edit splits' : 'Add split'}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenGLSplits(item)}
                                        sx={{ p: 0.5 }}
                                      >
                                        {item.has_splits ? <Edit size={14} /> : <Plus size={14} />}
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ px: 1 }}>
                              <Tooltip title="Edit expense item">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditItem(item)}
                                  sx={{ p: 0.5 }}
                                >
                                  <Edit size={14} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                        {reportData.items.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No expense items found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box sx={{ mt: 4, mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>Admin Signature</Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Admin Signature"
                        value={adminSignature}
                        onChange={(e) => setAdminSignature(e.target.value)}
                        disabled={isSigningReport || reportData?.report?.admin_signature}
                        helperText={reportData?.report?.admin_signature ? 'Report already signed' : 'Type your name to sign'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {!reportData?.report?.admin_signature && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleAdminSign}
                          disabled={isSigningReport || !adminSignature}
                        >
                          {isSigningReport ? 'Signing...' : 'Sign Report'}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>Actions</Typography>
                  

                  
                  <Grid container spacing={2}>
                    {canGenerateFinalPDF && (
                      <Grid item>
                        <Button
                          variant="contained"
                          startIcon={<FileText size={20} />}
                          onClick={handleGenerateFinalPDF}
                          disabled={generatingPDF}
                        >
                          {generatingPDF 
                            ? 'Generating...' 
                            : (reportData.report.workflow_status === 'final' || reportData.report.workflow_status === 'sent_to_billing')
                              ? 'Regenerate Final PDF'
                              : 'Generate Final PDF'
                          }
                        </Button>
                      </Grid>
                    )}

                    {reportData && reportData.report.workflow_status === 'final' && (
                      <Grid item>
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<Send size={20} />}
                          onClick={() => setSendToBillingDialog(true)}
                          disabled={sendingToBilling}
                        >
                          {sendingToBilling ? 'Sending...' : 'Send to Billing'}
                        </Button>
                      </Grid>
                    )}

                    {/* Re-send to Billing Button for already sent reports */}
                    {reportData && reportData.report.workflow_status === 'sent_to_billing' && (
                      <Grid item>
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<Send size={20} />}
                          onClick={() => setResendToBillingDialog(true)}
                          disabled={resendingToBilling}
                        >
                          {resendingToBilling ? 'Re-sending...' : 'Re-send to Billing'}
                        </Button>
                      </Grid>
                    )}
                  </Grid>

                  {!canEditGLCodes && 
                   !canGenerateFinalPDF && 
                   reportData.report.workflow_status !== 'final' && 
                   reportData.report.workflow_status !== 'sent_to_billing' && 
                   reportData.report.workflow_status === 'draft' && (
                    <Typography variant="body2" color="text.secondary">
                      No actions available for the current status
                    </Typography>
                  )}

                  {canEditGLCodes && reportData.items.some((item) => itemNeedsGlCode(item)) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <AlertTriangle size={16} style={{ marginRight: 8 }} />
                      All expense items need GL codes assigned before you can generate the final PDF. Web Portal lines are coded by the allocation below.
                    </Alert>
                  )}
                  <WebPortalAllocationPreview allocation={reportData.web_portal_allocation} />
                </Box>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <BankStatementViewer statement={reportData.bankStatement} />
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                {reportData.mrlItems && reportData.mrlItems.length > 0 ? (
                  <MissingReceiptLedger
                    reportId={reportData.report.id}
                    mrlItems={reportData.mrlItems}
                    setMrlItems={() => {}}
                    mrlSignature={reportData.report.mrl_signature}
                    setMrlSignature={() => {}}
                    readOnly={true}
                  />
                ) : (
                  <Alert severity="info">
                    No Missing Receipt Ledger entries for this report
                  </Alert>
                )}
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>Receipts ({reportData.receipts?.length || 0})</Typography>
                  
                  {reportData.receipts && reportData.receipts.length > 0 ? (
                    <Grid container spacing={2}>
                      {reportData.receipts.map((receipt) => (
                        <Grid item xs={12} sm={6} md={4} key={receipt.id}>
                          <Card variant="outlined">
                            <CardContent sx={{ pb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Image size={16} style={{ marginRight: 8 }} />
                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                  {receipt.original_filename}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Uploaded: {format(new Date(receipt.upload_date), 'MM/dd/yyyy')}
                              </Typography>
                            </CardContent>
                            <Box sx={{ p: 1, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                size="small"
                                onClick={() => window.open(receipt.url, '_blank')}
                                startIcon={<FileText size={16} />}
                              >
                                View
                              </Button>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No receipts found for this report
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            </Paper>
                    ) : (
            <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              {statusFilter === 'not_started' ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Users Who Haven't Started
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    These users have been sent bank statements but haven't started adding expenses or receipts to their reports.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    You may want to follow up with them to ensure timely submission.
                  </Typography>
                </Box>
              ) : statusFilter === 'no_bank_statement' ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Users Without Bank Statements
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    These users have no bank statements for the selected period, indicating they had no expenses.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No action is required for these users unless they later report expenses.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Select a report to view details
                </Typography>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Send to Billing Dialog */}
      <Dialog
        open={sendToBillingDialog}
        onClose={() => !sendingToBilling && setSendToBillingDialog(false)}
      >
        <DialogTitle>Send to Billing</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Enter the billing department's email address to send the finalized expense report.
          </Typography>
          <TextField
            fullWidth
            label="Billing Email"
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            error={!!error && error.includes('email')}
            helperText={error && error.includes('email') ? error : ''}
            margin="normal"
            disabled={sendingToBilling}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSendToBillingDialog(false)}
            disabled={sendingToBilling}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSendToBilling}
            disabled={sendingToBilling || !billingEmail.trim()}
            startIcon={sendingToBilling ? <CircularProgress size={20} /> : <Send size={20} />}
          >
            {sendingToBilling ? 'Sending...' : 'Send to Billing'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Send to Billing Dialog */}
      <Dialog
        open={confirmSendDialog}
        onClose={() => !markingAsSent && setConfirmSendDialog(false)}
      >
        <DialogTitle>Confirm Send to Billing</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to mark this report as sent to billing?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action confirms that you have sent the final PDF to the billing department
            and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmSendDialog(false)}
            disabled={markingAsSent}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleMarkSentToBilling}
            disabled={markingAsSent}
            startIcon={markingAsSent ? <CircularProgress size={20} /> : <Send size={20} />}
          >
            {markingAsSent ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-send to Billing Dialog */}
      <Dialog
        open={resendToBillingDialog}
        onClose={() => !resendingToBilling && setResendToBillingDialog(false)}
      >
        <DialogTitle>Re-send to Billing</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This report has already been sent to billing. Enter the email address to re-send the expense report.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The final PDF will be regenerated with any corrections you have made before sending.
          </Typography>
          <TextField
            fullWidth
            label="Billing Email"
            type="email"
            value={resendBillingEmail}
            onChange={(e) => setResendBillingEmail(e.target.value)}
            error={!!error && error.includes('email')}
            helperText={error && error.includes('email') ? error : ''}
            margin="normal"
            disabled={resendingToBilling}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setResendToBillingDialog(false)}
            disabled={resendingToBilling}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleResendToBilling}
            disabled={resendingToBilling || !resendBillingEmail.trim()}
            startIcon={resendingToBilling ? <CircularProgress size={20} /> : <Send size={20} />}
          >
            {resendingToBilling ? 'Re-sending...' : 'Re-send to Billing'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Expense Item Dialog */}
      <Dialog
        open={editItemDialog}
        onClose={() => !savingItemEdit && handleCancelItemEdit()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Expense Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={editItemData.date || ''}
                onChange={(e) => handleItemDataChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Work Order"
                value={editItemData.work_order || ''}
                onChange={(e) => handleItemDataChange('work_order', e.target.value)}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={editItemData.description || ''}
                onChange={(e) => handleItemDataChange('description', e.target.value)}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount"
                value={editItemData.amount || ''}
                onChange={(e) => handleItemDataChange('amount', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
                inputProps={{ step: 0.01, min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <GLCodeField
                idPrefix="edit-item"
                value={editItemData.gl_account || ''}
                onChange={(glAccount) => handleItemDataChange('gl_account', glAccount)}
                disabled={savingItemEdit}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelItemEdit}
            disabled={savingItemEdit}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveItemEdit}
            disabled={savingItemEdit}
            startIcon={savingItemEdit ? <CircularProgress size={16} /> : <Check size={16} />}
          >
            {savingItemEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GL Code Editor Dialog */}
      <Dialog
        open={glCodeDialog}
        onClose={() => !savingGLCodeDialog && setGLCodeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit GL Code</DialogTitle>
        <DialogContent>
          {glCodeDialogItem && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Date:</strong> {formatDateDisplay(glCodeDialogItem.date)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Amount:</strong> ${parseFloat(glCodeDialogItem.amount).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Description:</strong> {glCodeDialogItem.description}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <GLCodeField
                idPrefix="gl-dialog"
                value={glCodeDialogValue}
                onChange={setGLCodeDialogValue}
                disabled={savingGLCodeDialog}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGLCodeDialog(false)}
            disabled={savingGLCodeDialog}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveGLCodeDialog}
            disabled={savingGLCodeDialog}
            startIcon={savingGLCodeDialog ? <CircularProgress size={16} /> : <Check size={16} />}
          >
            {savingGLCodeDialog ? 'Saving...' : 'Save GL Code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GL Splits Dialog */}
      <Dialog
        open={glSplitsDialog}
        onClose={() => !savingGLSplits && setGLSplitsDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          Manage GL Code Splits
          {selectedItemForSplits && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedItemForSplits.description} - ${parseFloat(selectedItemForSplits.amount).toFixed(2)}
              </Typography>
              {glSplits.length > 1 && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Currently splitting into {glSplits.length} parts
                </Typography>
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          p: 3
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Split this expense item across multiple GL codes and branches. The total of all splits must equal the expense amount.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {glSplits.length > 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can adjust the percentages or amounts below. For example, change from 50% Portland / 50% Seattle to 75% Portland / 25% Seattle.
            </Alert>
          )}
          
          {/* Split Mode Selector */}
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Split Mode</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={splitMode === 'percentage' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSplitMode('percentage');
                  // Auto-distribute when switching modes if we have splits and an item
                  if (selectedItemForSplits && glSplits.length > 1) {
                    const itemAmount = parseFloat(selectedItemForSplits.amount);
                    const distributedSplits = autoDistributeRemaining(glSplits, itemAmount);
                    setGLSplits(distributedSplits);
                  }
                }}
                disabled={savingGLSplits}
                size="small"
              >
                By Percentage
              </Button>
              <Button
                variant={splitMode === 'dollar' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSplitMode('dollar');
                  // Auto-distribute when switching modes if we have splits and an item
                  if (selectedItemForSplits && glSplits.length > 1) {
                    const itemAmount = parseFloat(selectedItemForSplits.amount);
                    const distributedSplits = autoDistributeRemaining(glSplits, itemAmount);
                    setGLSplits(distributedSplits);
                  }
                }}
                disabled={savingGLSplits}
                size="small"
              >
                By Dollar Amount
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {splitMode === 'percentage' 
                ? 'Enter percentages and amounts will be calculated automatically. The system will auto-assign remaining percentage to new splits.'
                : 'Enter dollar amounts and percentages will be calculated automatically. The system will auto-assign remaining amount to new splits.'
              }
            </Typography>
          </Box>
          
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto',
            pr: 1
          }}>
          
          {glSplits.map((split, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2">Split {index + 1}</Typography>
                {glSplits.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveGLSplit(index)}
                    disabled={savingGLSplits}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                )}
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <GLCodeField
                    idPrefix={`split-${index}`}
                    value={split.gl_code}
                    onChange={(glCode) => handleGLSplitChange(index, 'gl_code', glCode)}
                    disabled={savingGLSplits}
                  />
                </Grid>
                
                {/* Primary input field based on split mode */}
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    fullWidth
                    label={splitMode === 'percentage' ? 'Percentage' : 'Amount'}
                    type="number"
                    value={splitMode === 'percentage' ? split.percentage : split.amount}
                    onChange={(e) => handleGLSplitChange(index, splitMode === 'percentage' ? 'percentage' : 'amount', e.target.value)}
                    disabled={savingGLSplits}
                    required
                    inputProps={splitMode === 'percentage' 
                      ? { step: 0.01, min: 0, max: 100 }
                      : { step: 0.01 }
                    }
                    InputProps={{
                      endAdornment: splitMode === 'percentage' ? '%' : '$'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: splitMode === 'percentage' ? '#e3f2fd' : '#f3e5f5'
                      }
                    }}
                  />
                </Grid>
                
                {/* Secondary calculated field */}
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    fullWidth
                    label={splitMode === 'percentage' ? 'Amount' : 'Percentage'}
                    type="number"
                    value={splitMode === 'percentage' ? split.amount : split.percentage}
                    disabled={true}
                    inputProps={splitMode === 'percentage'
                      ? { step: 0.01 }
                      : { step: 0.01, min: 0, max: 100 }
                    }
                    InputProps={{
                      endAdornment: splitMode === 'percentage' ? '$' : '%'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                    helperText="Auto-calculated"
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth required disabled={savingGLSplits || branchOptions.length === 0}>
                    <InputLabel id={`gl-split-branch-${index}`}>Split branch</InputLabel>
                    <Select
                      labelId={`gl-split-branch-${index}`}
                      label="Split branch"
                      value={branchOptions.some((branch) => branch.name === split.branch) ? split.branch : ''}
                      onChange={(e) => handleGLSplitChange(index, 'branch', e.target.value)}
                    >
                      <MenuItem value="" disabled>
                        Select a branch
                      </MenuItem>
                      {branchOptions.map((branch) => (
                        <MenuItem key={branch.name} value={branch.name}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '56px', justifyContent: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Split {index + 1}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={split.description}
                    onChange={(e) => handleGLSplitChange(index, 'description', e.target.value)}
                    disabled={savingGLSplits}
                    placeholder="Optional description for this split"
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
          
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Button
              startIcon={<Plus size={16} />}
              onClick={handleAddGLSplit}
              disabled={savingGLSplits}
              variant="outlined"
            >
              Add Split
            </Button>
            
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Total: ${glSplits.reduce((sum, split) => sum + parseFloat(split.amount || 0), 0).toFixed(2)}
              </Typography>
              {selectedItemForSplits && (
                <Typography variant="body2" color="text.secondary">
                  Target: ${parseFloat(selectedItemForSplits.amount).toFixed(2)}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {selectedItemForSplits?.has_splits && (
            <Button
              color="error"
              onClick={handleDeleteGLSplits}
              disabled={savingGLSplits}
              startIcon={<Trash2 size={16} />}
            >
              Delete All Splits
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setGLSplitsDialog(false)}
            disabled={savingGLSplits}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveGLSplits}
            disabled={savingGLSplits || branchOptions.length === 0}
            startIcon={savingGLSplits ? <CircularProgress size={16} /> : <Check size={16} />}
          >
            {savingGLSplits ? 'Saving...' : 'Save Splits'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpenseReportReviewer;